import { useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Loader2, CheckCircle2, XCircle, RotateCcw } from "lucide-react";

export const Quiz = ({ quiz, submitUrl }) => {
  const [answers, setAnswers] = useState({});
  const [result, setResult] = useState(null);
  const [submitting, setSubmitting] = useState(false);

  const answered = Object.keys(answers).length;

  const submit = async () => {
    setSubmitting(true);
    try {
      const { data } = await api.post(submitUrl, { answers: quiz.map((_, i) => answers[i] ?? -1) });
      setResult(data);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not submit the quiz.");
    } finally {
      setSubmitting(false);
    }
  };

  const reset = () => {
    setAnswers({});
    setResult(null);
  };

  return (
    <div className="border border-white/10" data-testid="quiz-section">
      <div className="border-b border-white/10 px-6 py-4">
        <h3 className="font-display text-lg font-bold uppercase tracking-tight">Self-Test</h3>
        <p className="mt-1 text-xs text-zinc-500">{quiz.length} questions · instant feedback</p>
      </div>
      {quiz.map((q, qi) => (
        <div key={q.id || qi} className="border-b border-white/5 px-6 py-6" data-testid={`quiz-question-${qi}`}>
          <p className="text-sm font-medium text-zinc-200 md:text-base">{qi + 1}. {q.question}</p>
          <div className="mt-4 grid grid-cols-1 gap-2 sm:grid-cols-2">
            {q.options.map((opt, oi) => {
              const selected = answers[qi] === oi;
              let cls = "border-white/15 text-zinc-300 hover:border-brand hover:text-white";
              if (result) {
                if (oi === result.answers[qi]) cls = "border-emerald-500/60 bg-emerald-500/10 text-emerald-400";
                else if (selected) cls = "border-red-500/60 bg-red-500/10 text-red-400";
                else cls = "border-white/10 text-zinc-600";
              } else if (selected) {
                cls = "border-brand bg-brand/10 text-white";
              }
              return (
                <button
                  key={oi}
                  type="button"
                  disabled={!!result}
                  onClick={() => setAnswers({ ...answers, [qi]: oi })}
                  data-testid={`quiz-q${qi}-opt${oi}`}
                  className={`flex items-center gap-3 border px-4 py-3 text-left text-sm transition-colors duration-200 ${cls}`}
                >
                  {result && oi === result.answers[qi] && <CheckCircle2 size={15} className="shrink-0" />}
                  {result && selected && oi !== result.answers[qi] && <XCircle size={15} className="shrink-0" />}
                  {opt}
                </button>
              );
            })}
          </div>
        </div>
      ))}
      <div className="flex items-center justify-between px-6 py-5">
        {result ? (
          <>
            <span className="font-display text-xl font-black tracking-tight" data-testid="quiz-score">
              Score: {result.score}/{result.total}
            </span>
            <button
              onClick={reset}
              data-testid="quiz-retry-button"
              className="flex items-center gap-2 border border-white/15 px-5 py-2.5 text-xs font-bold uppercase tracking-widest text-zinc-300 transition-colors duration-300 hover:border-brand hover:text-white"
            >
              <RotateCcw size={14} /> Retry
            </button>
          </>
        ) : (
          <>
            <span className="text-xs text-zinc-500">{answered}/{quiz.length} answered</span>
            <button
              onClick={submit}
              disabled={submitting || answered < quiz.length}
              data-testid="quiz-submit-button"
              className="flex items-center gap-2 bg-brand px-6 py-3 text-xs font-bold uppercase tracking-widest text-white transition-transform duration-300 hover:scale-95 disabled:opacity-50"
            >
              {submitting && <Loader2 size={14} className="animate-spin" />} Check Answers
            </button>
          </>
        )}
      </div>
    </div>
  );
};
